import { InputConstraints } from '@infotech-soe/erp-ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { FormContextState } from './FormContext';

export type InputOnValidate = (
  errors: InputConstraints,
  name?: string,
  isDirty?: boolean,
  limits?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    currentLength?: number;
  }
) => void;

const validationMessages = {
  tooLong: {
    id: 'core.form.validation.tooLong',
    defaultMessage: 'Value is too long: {currentLength}/{maxLength}',
  },
  tooShort: {
    id: 'core.form.validation.tooShort',
    defaultMessage: 'Value is too short: {currentLength}/{minLength}',
  },
  rangeOverflowed: {
    id: 'core.form.validation.rangeOverflowed',
    defaultMessage: 'Value is greater than the maximum allowed: {max}',
  },
  rangeUnderflowed: {
    id: 'core.form.validation.rangeUnderflowed',
    defaultMessage: 'Value is less than the minimum allowed: {min}',
  },
  patternFailed: {
    id: 'core.form.validation.patternFailed',
    defaultMessage: 'Value does not match the expected format',
  },
  customError: {
    id: 'core.form.validation.customError',
    defaultMessage: 'Value is invalid',
  },
} as const;

const validationConstraintMessages: ReadonlyArray<{
  constraint: InputConstraints;
  message: (typeof validationMessages)[keyof typeof validationMessages];
}> = [
  {
    constraint: InputConstraints.isTooLong,
    message: validationMessages.tooLong,
  },
  {
    constraint: InputConstraints.isTooShort,
    message: validationMessages.tooShort,
  },
  {
    constraint: InputConstraints.isRangeOverflowed,
    message: validationMessages.rangeOverflowed,
  },
  {
    constraint: InputConstraints.isRangeUnderflowed,
    message: validationMessages.rangeUnderflowed,
  },
  {
    constraint: InputConstraints.isPatternFailed,
    message: validationMessages.patternFailed,
  },
  {
    constraint: InputConstraints.isCustomError,
    message: validationMessages.customError,
  },
];

const hasConstraint = (
  errorsMask: InputConstraints,
  constraint: InputConstraints
): boolean =>
  constraint !== InputConstraints.isValid
    ? Math.floor(errorsMask / constraint) % 2 === 1
    : false;

export const useForm = ({
  isSubmitForbidden,
  isSubmitting,
  onBtnSave,
  onSubmit,
  onSubmitted,
}: {
  isSubmitForbidden: boolean;
  isSubmitting: boolean;
  onBtnSave: () => void;
  onSubmit: () => void;
  onSubmitted: () => void;
}): FormContextState => {
  const { formatMessage } = useIntl();
  const [isFormValid, setIsFormValid] = useState(true);
  const [errors, setErrors] = useState(
    () => new Map<string, InputConstraints>()
  );
  const [errorMessages, setErrorMessages] = useState(
    () => new Map<string, string>()
  );

  const handleValidate = useCallback<InputOnValidate>(
    (errorsMask, name, _isDirty, limits) => {
      if (name) {
        setErrors(prevErrors => {
          const newErrors = new Map(prevErrors);
          if (errorsMask === 0) {
            newErrors.delete(name);
          } else {
            newErrors.set(name, errorsMask);
          }
          return newErrors;
        });
        setErrorMessages(prevMessages => {
          const newMessages = new Map(prevMessages);
          if (errorsMask === 0) {
            newMessages.delete(name);
          } else {
            const reasons = validationConstraintMessages
              .filter(({ constraint }) => hasConstraint(errorsMask, constraint))
              .map(({ message }) =>
                formatMessage(message, {
                  min: limits?.min,
                  max: limits?.max,
                  minLength: limits?.minLength,
                  maxLength: limits?.maxLength,
                  currentLength: limits?.currentLength,
                })
              );
            const validationReason =
              reasons.length > 0 ? reasons.join('. ') : undefined;
            if (validationReason) {
              newMessages.set(name, validationReason);
            } else {
              newMessages.delete(name);
            }
          }
          return newMessages;
        });
      }
    },
    [formatMessage]
  );

  useEffect(() => {
    setIsFormValid(errors.size === 0);
  }, [errors]);

  const handleSubmit = useCallback(() => {
    if (errors.size === 0) onBtnSave?.();
  }, [errors.size, onBtnSave]);

  useEffect(() => {
    if (isSubmitting) {
      onSubmitted();
      if (!isSubmitForbidden) {
        onSubmit();
      }
    }
  }, [isSubmitting, isSubmitForbidden, onSubmitted, onSubmit]);

  return useMemo(
    () => ({
      errors,
      errorMessages,
      isFormValid,
      onSubmit: handleSubmit,
      onValidate: handleValidate,
    }),
    [errorMessages, errors, handleSubmit, handleValidate, isFormValid]
  );
};
